import axios from "axios";
import applyCaseMiddleware from "axios-case-converter";
import { jwtDecode } from "jwt-decode";

import { getActiveOutletId } from "./active-outlet";

// Custom camelize function
const customCamelize = (input) => {
  return input
    .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase()) // Convert _ followed by a letter to uppercase
    .replace(/_([0-9])/g, (_, number) => number) // Remove _ followed by a number
    .replace(
      /([0-9])_([a-z])/g,
      (_, number, letter) => `${number}${letter.toUpperCase()}`
    ); // Handle number followed by _ and then a letter
};

// Custom decamelize function
const customDecamelize = (input) => {
  return input
    .replace(/([a-z\d])([A-Z])/g, "$1_$2") // Insert underscore before each uppercase letter
    .replace(/([a-z])([0-9])/g, "$1_$2") // Insert underscore before each number
    .toLowerCase(); // Convert everything to lowercase
};

// Reusable function to create an API client
const createApiClient = ({
  additionalRequestInterceptor,
  baseURLSuffix = "",
}) => {
  const client = applyCaseMiddleware(
    axios.create({
      baseURL: `${import.meta.env.VITE_BASE_API_URL}${baseURLSuffix}`,
    }),
    {
      ignoreHeaders: true,
      caseFunctions: { camel: customCamelize, snake: customDecamelize },
    }
  );

  client.interceptors.request.use(
    (config) => {
      const accessToken = localStorage.getItem("access_token");

      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      if (additionalRequestInterceptor) {
        additionalRequestInterceptor(config);
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (!error.response || error.response.status !== 401) {
        return Promise.reject(error);
      }

      const token = localStorage.getItem("access_token");
      if (!token) {
        return Promise.reject(error);
      }

      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000; // Current time in seconds

      if (decodedToken.exp >= currentTime) {
        // Token is still valid
        return Promise.reject(error);
      }

      // Token has expired. Reject (instead of resolving with undefined) so the
      // caller's catch runs and getErrorMessage can say the session ended,
      // rather than the caller failing on `undefined` while the page reloads.
      localStorage.removeItem("access_token");
      window.location.href = "/";
      error.sessionExpired = true;
      return Promise.reject(error);
    }
  );

  return client;
};

export const apiClient = createApiClient({});

// Create apiOutletClient with additional Outlet-Id header and baseURLSuffix
export const apiOutletClient = createApiClient({
  baseURLSuffix: "/outlet",
  additionalRequestInterceptor: (config) => {
    if (!config.headers["Outlet-Id"]) {
      // Steady state: use the per-tab active outlet (mirrored from React auth
      // state). This tab's requests are never affected by another tab.
      const activeOutletId = getActiveOutletId();
      if (activeOutletId) {
        config.headers["Outlet-Id"] = activeOutletId;
      } else {
        // Pre-hydration only: before AuthProvider has restored the outlet on
        // first load, fall back to the persisted outlet. Once hydrated, the
        // in-memory value above is authoritative and localStorage is not read.
        const outlet = JSON.parse(localStorage.getItem("outlet"));
        if (outlet && outlet.id) {
          config.headers["Outlet-Id"] = outlet.id;
        }
      }
    }
  },
});
